import { z } from 'zod'
import { isSvBankCode } from '../data/sv-banks'
import { formatSvNit } from '../utils/sv-nit'
import {
  compactSvDui,
  formatSvDui,
  normalizeLegalEntityName,
  SV_DUI_DIGIT_LENGTH,
} from '../utils/sv-identity'
import { sanitizeName } from '../utils/sanitize'
import { organizationTaxIdSchema } from './organization.schema'

/**
 * Which document identifies the party being paid. Not a user choice — it
 * follows from whether the payee is a person or a legal entity.
 */
export type PayoutDocumentType = 'dui' | 'nit'

function compactBankAccount(value: string): string {
  return value.replace(/\D/g, '').slice(0, 24)
}

export function formatBankAccountInput(value: string): string {
  return compactBankAccount(value)
}

export function formatPayoutDocumentInput(value: string, documentType: PayoutDocumentType): string {
  return documentType === 'nit' ? formatSvNit(value) : formatSvDui(value)
}

const accountTypeSchema = z.enum(['savings', 'checking'])

const bankCodeSchema = z.string().trim().refine(isSvBankCode, 'validation.payout_bank_required')

const accountNumberSchema = z
  .string()
  .transform(compactBankAccount)
  .pipe(
    z
      .string()
      .min(6, 'validation.payout_account_number_min')
      .max(24, 'validation.payout_account_number_max')
      .regex(/^\d+$/, 'validation.payout_account_number_digits'),
  )

const personalHolderNameSchema = z
  .string()
  .transform(sanitizeName)
  .pipe(
    z
      .string()
      .min(3, 'validation.payout_holder_name_min')
      .max(120, 'validation.payout_holder_name_max'),
  )

const organizationHolderNameSchema = z
  .string()
  .transform((value) => normalizeLegalEntityName(value).trim())
  .pipe(
    z
      .string()
      .min(3, 'validation.payout_holder_name_min')
      .max(200, 'validation.payout_holder_name_max'),
  )

const payoutAccountBaseSchema = z.object({
  accountType: accountTypeSchema,
  bankCode: bankCodeSchema,
  accountNumber: accountNumberSchema,
  accountHolderName: personalHolderNameSchema,
})

/**
 * A natural person has one number, not a choice between two.
 *
 * In El Salvador the DUI now serves as the tax number for a natural person,
 * so "DUI or NIT" was never a question a host could answer wrongly — it was a
 * question with no wrong answer, offered anyway. Worse, picking the other
 * option cleared the field, so a host who tapped it to see what it meant lost
 * what they had already typed.
 *
 * A legal entity is the real distinction, and it lives in
 * `organizationPayoutAccountSchema` below: an agency has its own NIT, which is
 * not anyone's DUI.
 */
export const personalPayoutAccountSchema = payoutAccountBaseSchema
  .extend({
    documentType: z.literal('dui'),
    documentNumber: z.string(),
  })
  .superRefine((value, ctx) => {
    if (compactSvDui(value.documentNumber).length !== SV_DUI_DIGIT_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['documentNumber'],
        message: 'validation.payout_dui_format',
      })
    }
  })
  .transform((value) => ({
    ...value,
    documentNumber: formatSvDui(value.documentNumber),
  }))

export const organizationPayoutAccountSchema = payoutAccountBaseSchema
  .extend({
    accountHolderName: organizationHolderNameSchema,
    documentType: z.literal('nit'),
    documentNumber: organizationTaxIdSchema,
  })
  .transform((value) => ({
    ...value,
    documentNumber: formatSvNit(value.documentNumber),
  }))

type PersonalPayoutAccountValues = z.infer<typeof personalPayoutAccountSchema>
type OrganizationPayoutAccountValues = z.infer<typeof organizationPayoutAccountSchema>
export type PayoutAccountFormValues = PersonalPayoutAccountValues | OrganizationPayoutAccountValues
